'use client';

import { RotateCw, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { CubicRunStatus } from './pr-v2-sidebar-model';
import { Button } from '@/components/ui/button';
import { BetterTooltip } from '@/components/ui/tooltip';
import { useCancelAiReview } from '@/data/ai-review/use-cancel-ai-review';
import { useTriggerAiReview } from '@/data/ai-review/use-trigger-ai-review';
import { cn } from '@/lib/utils';
const RUN_AI_REVIEW_TOOLTIP = 'Run cubic AI review on this pull request.';
const CANCEL_AI_REVIEW_TOOLTIP = 'Cancel the in-progress cubic AI review.';

/**
 * cubic's review action beside its reviewer status: run a review, or cancel the ones in progress.
 * Shared by the PR page sidebar and the /code PR tab.
 */
export function CubicReviewRunButton({
  owner,
  repo,
  number,
  installationId,
  status,
  className,
  iconClassName
}: {
  owner: string;
  repo: string;
  number: string;
  installationId: number | null;
  status: CubicRunStatus;
  className?: string;
  iconClassName?: string;
}) {
  const {
    mutate: triggerAiReview,
    isPending: isReviewLoading
  } = useTriggerAiReview();
  const {
    mutateAsync: cancelAiReview,
    isPending: isCancelLoading
  } = useCancelAiReview();
  const [isCancellingReviews, setIsCancellingReviews] = useState(false);
  const handleTriggerAiReview = () => {
    if (!installationId) {
      toast.error('Missing installation ID for this pull request.');
      return;
    }
    triggerAiReview({
      owner,
      repo,
      number,
      installationId
    });
  };
  const handleCancelAiReview = async () => {
    if (!installationId) {
      toast.error('Missing installation ID for this pull request.');
      return;
    }
    if (status.runningTraceIds.length === 0) {
      return;
    }
    const traceIds = [...status.runningTraceIds];
    let failedCount = 0;
    setIsCancellingReviews(true);
    try {
      // Cancel all currently running traces so cubic's status stays consistent with its backend state.
      // Serialize mutations so a failed trace's cache rollback cannot undo another cancellation.
      for (const traceId of traceIds) {
        try {
          await cancelAiReview({
            owner,
            repo,
            number,
            installationId,
            traceId,
            suppressToast: true
          });
        } catch {
          failedCount += 1;
        }
      }
    } finally {
      setIsCancellingReviews(false);
    }
    if (failedCount === 0) {
      toast.success('AI review cancelled');
      return;
    }
    if (failedCount === traceIds.length) {
      toast.error('Failed to cancel AI review');
      return;
    }
    toast.error('Some AI review runs could not be cancelled');
  };
  const showCancelAction = status.status === 'running' && status.runningTraceIds.length > 0;
  return <BetterTooltip content={showCancelAction ? CANCEL_AI_REVIEW_TOOLTIP : RUN_AI_REVIEW_TOOLTIP}>
      <Button type="button" variant="ghost" size="icon" className={cn('size-6 text-zinc-500 hover:text-zinc-300', className)} onClick={showCancelAction ? handleCancelAiReview : handleTriggerAiReview} disabled={!installationId || isReviewLoading || isCancelLoading || isCancellingReviews} aria-label={showCancelAction ? 'Cancel AI review' : 'Run AI review'}>
        {showCancelAction ? <X className={iconClassName} /> : <RotateCw className={iconClassName} />}
      </Button>
    </BetterTooltip>;
}